import { useCallback, useState } from 'react';
import { Badge, SelectableChip, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ShieldCheck } from 'lucide-react';

import { useRoleBuilder } from '../../hooks/useRoleBuilder';
import { PermissionTree } from '../shared/PermissionTree';
import StudioCards from './StudioCards';

/**
 * Step 2: studio cards first; then, under a "Permissions" heading with the granted counter, one
 * chip per selected studio and the permission tree of the chosen chip. With no studio selected a
 * dashed box asks to pick one (as in the prototype).
 */
function PermissionsStep() {
  const { t } = useTranslation('people-access');
  const { studios, studioIds, permissionDraft, grantedCount, toggleStudio, togglePermission, setPermissionLevel } =
    useRoleBuilder();
  const [activeId, setActiveId] = useState(null);
  const selected = studios.filter((studio) => studioIds.includes(studio.id));
  // The studio just selected becomes the active chip; if the active one is deselected, the first remains.
  const active = selected.find((studio) => studio.id === activeId) ?? selected[0];
  const handleToggleStudio = useCallback(
    (studioId) => {
      if (!studioIds.includes(studioId)) setActiveId(studioId);
      toggleStudio(studioId);
    },
    [studioIds, toggleStudio],
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-sm font-semibold text-content">{t('roles.builder.permissions.studiosTitle')}</h2>
        <p className="mb-3 text-xs text-content-muted">{t('roles.builder.permissions.studiosHint')}</p>
        <StudioCards studios={studios} selectedIds={studioIds} onToggle={handleToggleStudio} />
      </div>
      {selected.length === 0 ? (
        <div className="rounded-lg border border-dashed border-edge py-10" data-testid="role-builder-pick-studio">
          <StatusMessage
            icon={ShieldCheck}
            title={t('roles.builder.permissions.pickStudio.title')}
            subtitle={t('roles.builder.permissions.pickStudio.subtitle')}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-content">{t('roles.builder.permissions.permissionsTitle')}</h2>
            <span aria-live="polite">
              <Badge size="small" variant={grantedCount > 0 ? 'success' : 'neutral'}>
                {t('roles.builder.permissions.granted', { count: grantedCount })}
              </Badge>
            </span>
          </div>
          <div role="group" aria-label={t('roles.builder.permissions.studioTabsAria')} className="flex flex-wrap gap-2">
            {selected.map((studio) => (
              <SelectableChip key={studio.id} selected={studio.id === active.id} onClick={() => setActiveId(studio.id)}>
                {studio.name}
              </SelectableChip>
            ))}
          </div>
          <section aria-label={active.name}>
            <PermissionTree
              key={active.id}
              nodes={active.tree}
              draft={permissionDraft}
              mode="edit"
              expand="top"
              onToggle={togglePermission}
              onLevelChange={setPermissionLevel}
            />
          </section>
        </div>
      )}
    </div>
  );
}

export default PermissionsStep;
